import { Injectable, NgZone } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from "@angular/common/http";
import { KeycloakService } from "./keycloak.service";
import { Observable, from, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";

/**
 * Re-deliver Observable events inside Angular's NgZone.
 * Keycloak updateToken / XHR can emit outside the zone → subscribers
 * mutate state but the view stays empty, and Router warns on navigateByUrl.
 */
function runInZone<T>(ngZone: NgZone) {
  return (source: Observable<T>) =>
    new Observable<T>((subscriber) =>
      source.subscribe({
        next: (value) => ngZone.run(() => subscriber.next(value)),
        error: (err) => ngZone.run(() => subscriber.error(err)),
        complete: () => ngZone.run(() => subscriber.complete()),
      })
    );
}

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(
    private keycloakService: KeycloakService,
    private ngZone: NgZone
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return from(this.keycloakService.ensureToken(30)).pipe(
      catchError(() => from(Promise.resolve(this.keycloakService.getToken() || ""))),
      switchMap((token) => next.handle(this.withToken(request, token))),
      runInZone(this.ngZone),
      catchError((error) => {
        if (error.status !== 401) {
          return throwError(error);
        }
        return this.refreshAndRetry(request, next);
      })
    );
  }

  private refreshAndRetry(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return from(this.keycloakService.ensureToken(-1)).pipe(
      switchMap((token) => {
        if (!token) {
          return throwError(new Error("empty access token after refresh"));
        }
        return next.handle(this.withToken(request, token));
      }),
      runInZone(this.ngZone),
      catchError((error) => {
        if (error && error.status === 401) {
          console.warn("API 401 after token refresh", request.url);
          return throwError(error);
        }
        console.warn(
          "Refresh token rejected — redirecting to login",
          request.url,
          error
        );
        this.keycloakService.login();
        return throwError(error);
      })
    );
  }

  private withToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: "Bearer " + (token || ""),
        accept: "*/*",
      },
    });
  }
}
